/**
 * Resolves environment-specific API URLs so that the Azure Functions endpoint
 * and the WebSocket server always target the same backend environment.
 *
 * Problem: If REACT_APP_WEBSOCKET_URL is set (e.g. to wss://ws.qolsync.ai for
 * production) but REACT_APP_FUNCTIONS_URL is not, the Functions URL would fall back
 * to the staging server (realtranslation-func-dev-eastus2.azurewebsites.net). Sessions created by staging
 * Functions are stored in staging Redis; the production WebSocket server queries
 * production Redis, finds no session, and rejects the connection with HTTP 400.
 * The client sees WebSocket close code 1006 ("session stopped immediately").
 *
 * Fix: When REACT_APP_FUNCTIONS_URL is not explicitly set, infer the correct
 * Functions URL from the WebSocket URL so both always share the same environment.
 */

/** Hard-coded staging endpoint used when nothing usable is configured. */
const STAGING_FUNCTIONS_URL = 'https://realtranslation-func-dev-eastus2.azurewebsites.net/api';

/**
 * Resolves the Azure Functions base URL AND reports whether it was actually resolved.
 *
 * `resolved` is false only when the helper falls through to STAGING_FUNCTIONS_URL — that
 * fall-through IS the hidden staging fallback, not a deliberate target. A surface that
 * WRITES platform configuration must refuse to save in that case: an operator would
 * otherwise believe they had repointed production. Staging AdminPortal sets
 * REACT_APP_FUNCTIONS_URL explicitly, so a real staging deploy still reports true.
 *
 * @param {string} reactAppFunctionsUrl - Value of process.env.REACT_APP_FUNCTIONS_URL
 * @param {string} reactAppWebsocketUrl - Value of process.env.REACT_APP_WEBSOCKET_URL
 * @param {boolean} isLocalDev - True when running on localhost / 127.0.0.1
 * @returns {{baseUrl: string, resolved: boolean}} The base URL (no trailing slash) and whether it was resolved
 */
export function resolveFunctionsTarget(reactAppFunctionsUrl, reactAppWebsocketUrl, isLocalDev) {
    if (reactAppFunctionsUrl) {
        return { baseUrl: reactAppFunctionsUrl.replace(/\/$/, ''), resolved: true };
    }
    if (isLocalDev) return { baseUrl: 'http://localhost:7071/api', resolved: true };
    try {
        const { hostname } = new URL(reactAppWebsocketUrl);
        if (hostname.toLowerCase() === 'ws.qolsync.ai') {
            return { baseUrl: 'https://func.qolsync.ai/api', resolved: true };
        }
    } catch (e) {
        // Fall through to the default staging endpoint when the WebSocket URL
        // is missing or invalid.
    }
    return { baseUrl: STAGING_FUNCTIONS_URL, resolved: false };
}
